import cytoscape, { type Core, type ElementDefinition } from "cytoscape";
import fcose from "cytoscape-fcose";
import { useEffect, useImperativeHandle, useRef, forwardRef } from "react";
import type { GraphCore, GraphNodeRecord } from "../data/buildGraphData";

cytoscape.use(fcose);

export type GraphCanvasHandle = {
  fit: () => void;
  relayout: () => void;
  reset: () => void;
  exportPng: () => void;
  toggleFullscreen: () => void;
};

type Position = { x: number; y: number };

type GraphCanvasProps = {
  graph: GraphCore;
  positions: Record<string, Position>;
  selectedId?: string;
  relationKinds: Set<string>;
  refineLayout: boolean;
  onSelect: (node: GraphNodeRecord) => void;
  onStatus: (message: string) => void;
};

const moduleColors: Record<string, string> = {
  "module-1": "#2f6f61",
  "module-2": "#b5523b",
  "module-3": "#3973a6",
  "module-4": "#8a6731",
  "module-5": "#6b5a91",
  "module-6": "#47715a",
};

const stageColors: Record<string, string> = {
  "stage-1": "#286b5d",
  "stage-2": "#a94736",
  "stage-3": "#2e6d9f",
  "stage-4": "#8a641e",
  "stage-5": "#69558c",
  "stage-6": "#386c50",
};

function graphElements(
  graph: GraphCore,
  positions: Record<string, Position>,
  relationKinds: Set<string>,
): ElementDefinition[] {
  const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
  const nodes = graph.nodes.map((node) => ({
    data: {
      ...node,
      displayLabel: node.kind === "module" ? node.label.replace("｜", "\n") : node.label,
      shortLabel:
        node.kind === "knowledge"
          ? `${node.number}`
          : node.kind === "question"
            ? node.id
            : node.label,
      color: moduleColors[node.moduleId ?? ""] ?? "#66716a",
      fillColor: node.kind === "question"
        ? stageColors[node.stageId ?? ""] ?? "#39443f"
        : moduleColors[node.moduleId ?? ""] ?? "#66716a",
    },
    position: positions[node.id],
  }));
  const edges = graph.edges
    .filter((edge) => edge.layer !== "semantic" || relationKinds.has(edge.kind))
    .map((edge) => {
      const source = nodeById.get(edge.source);
      return {
        data: {
          ...edge,
          weight: edge.weight ?? 1,
          edgeColor: edge.layer === "mainline"
            ? stageColors[source?.stageId ?? ""] ?? "#8d6c2f"
            : undefined,
        },
      };
    });
  return [...nodes, ...edges];
}

function runLayout(cy: Core, positions: Record<string, Position>, refine: boolean) {
  cy.nodes().positions((node) => positions[node.id()] ?? node.position());
  if (!refine || cy.nodes().length > 260) {
    cy.fit(undefined, 68);
    return;
  }

  const fixedNodeConstraint = cy
    .nodes("[kind = 'module']")
    .map((node) => ({ nodeId: node.id(), position: positions[node.id()] ?? node.position() }));

  cy.layout({
    name: "fcose",
    quality: "default",
    randomize: false,
    animate: false,
    fit: true,
    padding: 56,
    nodeRepulsion: 9000,
    idealEdgeLength: 122,
    edgeElasticity: 0.32,
    numIter: 450,
    fixedNodeConstraint,
  } as cytoscape.LayoutOptions).run();
}

export const GraphCanvas = forwardRef<GraphCanvasHandle, GraphCanvasProps>(
  function GraphCanvas(
    { graph, positions, selectedId, relationKinds, refineLayout, onSelect, onStatus },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const cyRef = useRef<Core | null>(null);

    useEffect(() => {
      if (!containerRef.current) return;
      const cy = cytoscape({
        container: containerRef.current,
        elements: graphElements(graph, positions, relationKinds),
        layout: { name: "preset" },
        minZoom: 0.18,
        maxZoom: 3.2,
        selectionType: "single",
        boxSelectionEnabled: true,
        style: [
          {
            selector: "node",
            style: {
              "background-color": "data(fillColor)",
              "border-color": "#ffffff",
              "border-width": 2,
              color: "#24312c",
              "font-family": "Microsoft YaHei",
              "font-size": 11,
              "text-wrap": "wrap",
              "text-max-width": "116px",
              "text-valign": "bottom",
              "text-margin-y": 8,
              label: "data(displayLabel)",
              width: 24,
              height: 24,
            },
          },
          {
            selector: "node[kind = 'module']",
            style: {
              shape: "ellipse",
              width: 124,
              height: 82,
              "font-size": 13,
              "font-weight": 700,
              color: "#ffffff",
              label: "data(label)",
              "text-valign": "center",
              "text-margin-y": 0,
              "text-max-width": "132px",
            },
          },
          {
            selector: "node[kind = 'submodule']",
            style: {
              shape: "round-rectangle",
              width: 138,
              height: 40,
              "background-color": "#fbfcfa",
              "border-color": "data(color)",
              "border-width": 2,
              "font-size": 12,
              label: "data(label)",
              "text-valign": "center",
              "text-margin-y": 0,
              "text-max-width": "106px",
            },
          },
          {
            selector: "node[kind = 'question']",
            style: {
              shape: "round-rectangle",
              width: 46,
              height: 27,
              "background-color": "data(fillColor)",
              color: "#ffffff",
              "border-color": "#ffffff",
              "border-width": 2,
              "font-size": 10,
              label: "data(shortLabel)",
              "text-valign": "center",
              "text-margin-y": 0,
            },
          },
          {
            selector: "node:selected",
            style: {
              "border-color": "#d59a29",
              "border-width": 5,
              "overlay-color": "#d59a29",
              "overlay-opacity": 0.08,
              "overlay-padding": 10,
            },
          },
          {
            selector: ".is-dimmed",
            style: { opacity: 0.08 },
          },
          {
            selector: "edge.is-emphasized",
            style: { opacity: 1, width: 4 },
          },
          {
            selector: "edge",
            style: {
              width: 1.6,
              "line-color": "#9aa69f",
              "target-arrow-color": "#9aa69f",
              "target-arrow-shape": "triangle",
              "arrow-scale": 0.55,
              "curve-style": "bezier",
              opacity: 0.74,
            },
          },
          {
            selector: "edge[layer = 'structure']",
            style: { "line-style": "solid", "target-arrow-shape": "none", width: 2.4, opacity: 0.78, "line-color": "#87958d" },
          },
          {
            selector: "edge[layer = 'mainline']",
            style: { "line-style": "solid", opacity: 0.64, width: "mapData(weight, 1, 20, 1.4, 6)", "line-color": "data(edgeColor)", "target-arrow-color": "data(edgeColor)", "curve-style": "unbundled-bezier", "control-point-distances": 28, "control-point-weights": 0.5 },
          },
          {
            selector: "edge[kind = 'prerequisite']",
            style: { "line-color": "#2f6f61", "target-arrow-color": "#2f6f61", width: 2 },
          },
          {
            selector: "edge[kind = 'method_support']",
            style: { "line-color": "#3973a6", "target-arrow-color": "#3973a6", width: 2 },
          },
          {
            selector: "edge[kind = 'application']",
            style: { "line-color": "#b5523b", "target-arrow-color": "#b5523b", width: 2 },
          },
          {
            selector: "edge[kind = 'falsification']",
            style: { "line-color": "#8a6731", "target-arrow-color": "#8a6731", width: 2, "line-style": "dashed" },
          },
        ],
      });
      cyRef.current = cy;

      cy.on("tap", "node", (event) => onSelect(event.target.data() as GraphNodeRecord));
      cy.on("mouseover", "node", (event) => {
        const neighborhood = event.target.closedNeighborhood();
        cy.elements().difference(neighborhood).addClass("is-dimmed");
        neighborhood.edges().addClass("is-emphasized");
      });
      cy.on("mouseout", "node", () => {
        cy.elements().removeClass("is-dimmed is-emphasized");
      });
      cy.on("tap", "edge", (event) => {
        const label = event.target.data("rationale") as string | undefined;
        if (label) onStatus(label);
      });
      cy.on("mouseover", "edge", (event) => {
        const rationale = event.target.data("rationale") as string | undefined;
        if (rationale) onStatus(rationale);
      });
      cy.on("mouseout", "edge", () => onStatus("拖动画布探索；悬停语义边查看审核理由。"));

      runLayout(cy, positions, refineLayout);
      return () => {
        cy.destroy();
        cyRef.current = null;
      };
    }, [graph, positions, relationKinds, refineLayout, onSelect, onStatus]);

    useEffect(() => {
      if (!selectedId || !cyRef.current) return;
      const target = cyRef.current.getElementById(selectedId);
      if (target.nonempty()) {
        target.select();
        cyRef.current.animate({ center: { eles: target }, zoom: Math.max(cyRef.current.zoom(), 1.05) }, { duration: 280 });
      }
    }, [selectedId]);

    useImperativeHandle(ref, () => ({
      fit: () => cyRef.current?.fit(undefined, 56),
      relayout: () => cyRef.current && runLayout(cyRef.current, positions, true),
      reset: () => cyRef.current && runLayout(cyRef.current, positions, false),
      exportPng: () => {
        const data = cyRef.current?.png({ full: true, scale: 2, bg: "#f5f6f2" });
        if (!data) return;
        const link = document.createElement("a");
        link.href = data;
        link.download = "quant-knowledge-graph-current-view.png";
        link.click();
      },
      toggleFullscreen: () => {
        if (document.fullscreenElement) void document.exitFullscreen();
        else void containerRef.current?.parentElement?.requestFullscreen();
      },
    }), [positions]);

    return <div ref={containerRef} className="graph-canvas" data-testid="graph-canvas" />;
  },
);
